import { useRef, useState, useEffect, useCallback } from 'react';
import { getEdgeFunctionHeaders } from '@/lib/edgeFunctions';

interface Props {
  imageUrl: string;
  onSave: (newImageUrl: string) => void;
  onClose: () => void;
}

// Brush sizes in CANVAS pixels (image-space). These are zoom-independent —
// the brush always covers the same area of the image regardless of zoom level.
// On-screen the ring appears as brushSize * zoom pixels wide.
const BRUSH_SIZES = [8, 16, 30, 50, 80];
const BRUSH_LABELS = ['XS', 'S', 'M', 'L', 'XL'];

type QualityMode = 'standard' | 'high';
type ToolMode = 'erase' | 'restore';
type AppState = 'painting' | 'processing' | 'done' | 'error';

export default function EraseModal({ imageUrl, onSave, onClose }: Props) {
  const displayCanvasRef = useRef<HTMLCanvasElement>(null);
  const maskCanvasRef = useRef<HTMLCanvasElement>(null);
  const origCanvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const [brushSize, setBrushSize] = useState(30);
  const [tool, setTool] = useState<ToolMode>('erase');
  const [qualityMode, setQualityMode] = useState<QualityMode>('standard');
  const [appState, setAppState] = useState<AppState>('painting');
  const [errorMsg, setErrorMsg] = useState('');
  const [zoom, setZoom] = useState(1);
  const [cursorPos, setCursorPos] = useState<{ x: number; y: number } | null>(null);
  const [hasMask, setHasMask] = useState(false);
  const [progressMsg, setProgressMsg] = useState('Analyzing image...');
  const [canUndo, setCanUndo] = useState(false);

  const isErasing = useRef(false);
  const lastPosRef = useRef<{ x: number; y: number } | null>(null);
  const maskHistoryRef = useRef<ImageData[]>([]);
  const imgSizeRef = useRef({ w: 0, h: 0 });
  const zoomRef = useRef(zoom);
  useEffect(() => { zoomRef.current = zoom; }, [zoom]);

  // ── Load image ────────────────────────────────────────────────
  useEffect(() => {
    const displayCanvas = displayCanvasRef.current;
    const maskCanvas = maskCanvasRef.current;
    const origCanvas = origCanvasRef.current;
    if (!displayCanvas || !maskCanvas || !origCanvas) return;

    const loadImage = (src: string, crossOrigin: boolean) => {
      const img = new Image();
      if (crossOrigin) img.crossOrigin = 'anonymous';
      img.onload = () => {
        const w = img.naturalWidth;
        const h = img.naturalHeight;
        imgSizeRef.current = { w, h };

        displayCanvas.width = w;
        displayCanvas.height = h;
        maskCanvas.width = w;
        maskCanvas.height = h;
        origCanvas.width = w;
        origCanvas.height = h;

        const dCtx = displayCanvas.getContext('2d')!;
        const oCtx = origCanvas.getContext('2d')!;
        const mCtx = maskCanvas.getContext('2d')!;

        dCtx.drawImage(img, 0, 0);
        oCtx.drawImage(img, 0, 0);
        mCtx.fillStyle = '#000000';
        mCtx.fillRect(0, 0, w, h);

        maskHistoryRef.current = [mCtx.getImageData(0, 0, w, h)];
        setCanUndo(false);
        setHasMask(false);

        // Auto-fit zoom based on container
        const container = containerRef.current;
        if (container) {
          const maxW = container.clientWidth - 80;
          const maxH = container.clientHeight - 80;
          const fit = Math.min(1, maxW / w, maxH / h);
          setZoom(fit > 0 ? fit : 1);
        }
      };
      img.onerror = () => { if (crossOrigin) loadImage(src, false); };
      img.src = src;
    };

    loadImage(imageUrl, true);
  }, [imageUrl]);

  // ── Redraw display canvas ─────────────────────────────────────
  const redrawDisplay = useCallback(() => {
    const displayCanvas = displayCanvasRef.current;
    const maskCanvas = maskCanvasRef.current;
    const origCanvas = origCanvasRef.current;
    if (!displayCanvas || !maskCanvas || !origCanvas) return;

    const dCtx = displayCanvas.getContext('2d')!;
    const mCtx = maskCanvas.getContext('2d', { willReadFrequently: true })!;
    const { w, h } = imgSizeRef.current;
    if (!w || !h) return;

    dCtx.drawImage(origCanvas, 0, 0);

    const maskData = mCtx.getImageData(0, 0, w, h);
    const md = maskData.data;

    const tempCanvas = document.createElement('canvas');
    tempCanvas.width = w;
    tempCanvas.height = h;
    const tCtx = tempCanvas.getContext('2d')!;
    const outData = tCtx.createImageData(w, h);
    const od = outData.data;

    for (let i = 0; i < md.length; i += 4) {
      if (md[i] > 128) {
        od[i] = 255;
        od[i + 1] = 60;
        od[i + 2] = 60;
        od[i + 3] = 140;
      }
    }
    tCtx.putImageData(outData, 0, 0);
    dCtx.drawImage(tempCanvas, 0, 0);
  }, []);

  // ── Convert screen coords → canvas pixel coords ───────────────
  // KEY: canvas is displayed at naturalWidth*zoom CSS pixels (no CSS transform).
  // getBoundingClientRect() on the canvas gives us its exact viewport position.
  // canvasX = (clientX - rect.left) * (canvas.width / rect.width)
  // This is more robust than dividing by zoom directly, as it accounts for
  // any subpixel rounding in CSS layout.
  const getCanvasPos = useCallback((clientX: number, clientY: number) => {
    const canvas = displayCanvasRef.current;
    if (!canvas) return { x: 0, y: 0 };
    const rect = canvas.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) return { x: 0, y: 0 };
    return {
      x: (clientX - rect.left) * (canvas.width / rect.width),
      y: (clientY - rect.top) * (canvas.height / rect.height),
    };
  }, []);

  // ── Paint mask ────────────────────────────────────────────────
  const paintMask = useCallback((cx: number, cy: number, fromX?: number, fromY?: number) => {
    const maskCanvas = maskCanvasRef.current;
    if (!maskCanvas) return;
    const mCtx = maskCanvas.getContext('2d')!;

    // brushSize is in SCREEN pixels. Convert to canvas pixels:
    // canvasRadius = (brushSize / zoom) / 2
    // BUT: at low zoom this makes the brush enormous on the canvas.
    // Instead, treat brushSize as canvas pixels directly (zoom-independent).
    // This means the brush covers the same image area regardless of zoom,
    // and the on-screen ring size = brushSize * zoom (scales with zoom).
    const canvasBrushRadius = brushSize / 2;

    const paint = (x: number, y: number) => {
      mCtx.beginPath();
      mCtx.arc(x, y, canvasBrushRadius, 0, Math.PI * 2);
      mCtx.fillStyle = tool === 'erase' ? '#ffffff' : '#000000';
      mCtx.fill();
    };

    if (fromX !== undefined && fromY !== undefined) {
      const dist = Math.sqrt((cx - fromX) ** 2 + (cy - fromY) ** 2);
      // Step size based on canvas brush radius for smooth strokes
      const steps = Math.max(1, Math.ceil(dist / (canvasBrushRadius * 0.5)));
      for (let i = 0; i <= steps; i++) {
        const t = i / steps;
        paint(fromX + (cx - fromX) * t, fromY + (cy - fromY) * t);
      }
    } else {
      paint(cx, cy);
    }

    redrawDisplay();

    const { w, h } = imgSizeRef.current;
    if (w && h) {
      const data = mCtx.getImageData(0, 0, w, h).data;
      let hasWhite = false;
      for (let i = 0; i < data.length; i += 4) {
        if (data[i] > 128) { hasWhite = true; break; }
      }
      setHasMask(hasWhite);
    }
  }, [brushSize, tool, redrawDisplay]);

  // ── Mouse handlers ────────────────────────────────────────────
  const handleMouseDown = useCallback((e: React.MouseEvent<HTMLCanvasElement>) => {
    e.preventDefault();
    const maskCanvas = maskCanvasRef.current;
    if (maskCanvas) {
      const mCtx = maskCanvas.getContext('2d', { willReadFrequently: true })!;
      const { w, h } = imgSizeRef.current;
      if (w && h) {
        const snap = mCtx.getImageData(0, 0, w, h);
        maskHistoryRef.current = [...maskHistoryRef.current.slice(-29), snap];
        setCanUndo(maskHistoryRef.current.length > 1);
      }
    }
    isErasing.current = true;
    const pos = getCanvasPos(e.clientX, e.clientY);
    lastPosRef.current = pos;
    paintMask(pos.x, pos.y);
  }, [getCanvasPos, paintMask]);

  const handleMouseMove = useCallback((e: React.MouseEvent<HTMLCanvasElement>) => {
    // Cursor ring: position relative to the canvas wrapper div.
    // The wrapper is exactly the same size as the canvas (both = imgW*zoom × imgH*zoom).
    // We use getBoundingClientRect() on the canvas to get its exact viewport position,
    // then subtract to get the offset within the canvas/wrapper.
    const canvas = displayCanvasRef.current;
    if (canvas) {
      const rect = canvas.getBoundingClientRect();
      // Clamp to canvas bounds so ring doesn't fly off
      const x = Math.max(0, Math.min(rect.width, e.clientX - rect.left));
      const y = Math.max(0, Math.min(rect.height, e.clientY - rect.top));
      setCursorPos({ x, y });
    }
    if (!isErasing.current) return;
    const pos = getCanvasPos(e.clientX, e.clientY);
    const last = lastPosRef.current;
    paintMask(pos.x, pos.y, last?.x, last?.y);
    lastPosRef.current = pos;
  }, [getCanvasPos, paintMask]);

  const handleMouseUp = useCallback(() => {
    isErasing.current = false;
    lastPosRef.current = null;
  }, []);

  const handleMouseLeave = useCallback(() => {
    isErasing.current = false;
    lastPosRef.current = null;
    setCursorPos(null);
  }, []);

  // ── Touch handlers ────────────────────────────────────────────
  const handleTouchStart = useCallback((e: React.TouchEvent<HTMLCanvasElement>) => {
    e.preventDefault();
    const touch = e.touches[0];
    const maskCanvas = maskCanvasRef.current;
    if (maskCanvas) {
      const mCtx = maskCanvas.getContext('2d', { willReadFrequently: true })!;
      const { w, h } = imgSizeRef.current;
      if (w && h) {
        const snap = mCtx.getImageData(0, 0, w, h);
        maskHistoryRef.current = [...maskHistoryRef.current.slice(-29), snap];
        setCanUndo(maskHistoryRef.current.length > 1);
      }
    }
    isErasing.current = true;
    const pos = getCanvasPos(touch.clientX, touch.clientY);
    lastPosRef.current = pos;
    paintMask(pos.x, pos.y);
  }, [getCanvasPos, paintMask]);

  const handleTouchMove = useCallback((e: React.TouchEvent<HTMLCanvasElement>) => {
    e.preventDefault();
    if (!isErasing.current) return;
    const touch = e.touches[0];
    const pos = getCanvasPos(touch.clientX, touch.clientY);
    const last = lastPosRef.current;
    paintMask(pos.x, pos.y, last?.x, last?.y);
    lastPosRef.current = pos;
  }, [getCanvasPos, paintMask]);

  const handleTouchEnd = useCallback(() => {
    isErasing.current = false;
    lastPosRef.current = null;
  }, []);

  // ── Undo ──────────────────────────────────────────────────────
  const undo = useCallback(() => {
    const maskCanvas = maskCanvasRef.current;
    if (!maskCanvas || maskHistoryRef.current.length <= 1) return;
    const mCtx = maskCanvas.getContext('2d')!;
    maskHistoryRef.current = maskHistoryRef.current.slice(0, -1);
    const prev = maskHistoryRef.current[maskHistoryRef.current.length - 1];
    mCtx.putImageData(prev, 0, 0);
    setCanUndo(maskHistoryRef.current.length > 1);
    redrawDisplay();
    const { w, h } = imgSizeRef.current;
    if (w && h) {
      const data = mCtx.getImageData(0, 0, w, h).data;
      let hasWhite = false;
      for (let i = 0; i < data.length; i += 4) {
        if (data[i] > 128) { hasWhite = true; break; }
      }
      setHasMask(hasWhite);
    }
  }, [redrawDisplay]);

  // ── Clear mask ────────────────────────────────────────────────
  const clearMask = useCallback(() => {
    const maskCanvas = maskCanvasRef.current;
    if (!maskCanvas) return;
    const mCtx = maskCanvas.getContext('2d')!;
    const { w, h } = imgSizeRef.current;
    if (!w || !h) return;
    const snap = mCtx.getImageData(0, 0, w, h);
    maskHistoryRef.current = [...maskHistoryRef.current.slice(-29), snap];
    mCtx.fillStyle = '#000000';
    mCtx.fillRect(0, 0, w, h);
    redrawDisplay();
    setHasMask(false);
    setCanUndo(true);
  }, [redrawDisplay]);

  // ── Build mask blob ───────────────────────────────────────────
  const buildMaskBlob = useCallback((): Promise<Blob> => {
    return new Promise((resolve, reject) => {
      const maskCanvas = maskCanvasRef.current;
      const { w, h } = imgSizeRef.current;
      if (!maskCanvas || !w || !h) { reject(new Error('No mask')); return; }
      const mCtx = maskCanvas.getContext('2d', { willReadFrequently: true })!;
      const maskData = mCtx.getImageData(0, 0, w, h);
      const md = maskData.data;
      const outCanvas = document.createElement('canvas');
      outCanvas.width = w;
      outCanvas.height = h;
      const oCtx = outCanvas.getContext('2d')!;
      const outData = oCtx.createImageData(w, h);
      const od = outData.data;
      for (let i = 0; i < md.length; i += 4) {
        const isMasked = md[i] > 128;
        od[i] = 0; od[i + 1] = 0; od[i + 2] = 0;
        od[i + 3] = isMasked ? 0 : 255;
      }
      oCtx.putImageData(outData, 0, 0);
      outCanvas.toBlob((blob) => {
        if (blob) resolve(blob);
        else reject(new Error('Failed to create mask blob'));
      }, 'image/png');
    });
  }, []);

  // ── Build source blob ─────────────────────────────────────────
  const buildSourceBlob = useCallback((): Promise<Blob> => {
    return new Promise((resolve, reject) => {
      const origCanvas = origCanvasRef.current;
      if (!origCanvas) { reject(new Error('No source')); return; }
      origCanvas.toBlob((blob) => {
        if (blob) resolve(blob);
        else reject(new Error('Failed to create source blob'));
      }, 'image/png');
    });
  }, []);

  // ── AI Inpaint ────────────────────────────────────────────────
  const handleSmartErase = useCallback(async () => {
    if (!hasMask) return;
    setAppState('processing');
    setErrorMsg('');

    const msgs = qualityMode === 'high'
      ? ['Analyzing selection...', 'Running AI inpainting...', 'Refining edges...', 'Finalizing result...']
      : ['Analyzing selection...', 'Running AI removal...', 'Finalizing...'];

    let msgIdx = 0;
    setProgressMsg(msgs[0]);
    const msgInterval = setInterval(() => {
      msgIdx = Math.min(msgIdx + 1, msgs.length - 1);
      setProgressMsg(msgs[msgIdx]);
    }, qualityMode === 'high' ? 4000 : 3000);

    try {
      const [sourceBlob, maskBlob] = await Promise.all([buildSourceBlob(), buildMaskBlob()]);

      const toBase64 = (blob: Blob): Promise<string> =>
        new Promise((res, rej) => {
          const reader = new FileReader();
          reader.onload = () => { res((reader.result as string).split(',')[1]); };
          reader.onerror = rej;
          reader.readAsDataURL(blob);
        });

      const [sourceB64, maskB64] = await Promise.all([toBase64(sourceBlob), toBase64(maskBlob)]);

      const supabaseUrl = import.meta.env.VITE_PUBLIC_SUPABASE_URL as string;

      const response = await fetch(`${supabaseUrl}/functions/v1/edit-ad-image`, {
        method: 'POST',
        headers: await getEdgeFunctionHeaders(),
        body: JSON.stringify({
          imageBase64: sourceB64,
          maskBase64: maskB64,
          editMode: 'inpaint',
          qualityMode,
          editPrompt: qualityMode === 'high'
            ? 'Remove the selected object completely. Reconstruct the background with photorealistic detail — match surrounding textures, lighting, shadows, colors, and depth. The result must look completely natural as if the object never existed. High quality multi-pass inpainting.'
            : 'Remove the selected object and fill the area with the natural background. Match surrounding textures and lighting seamlessly.',
        }),
      });

      clearInterval(msgInterval);

      if (!response.ok) {
        const err = await response.json().catch(() => ({ error: 'Request failed' }));
        throw new Error(err.error ?? 'AI inpainting failed');
      }

      const result = await response.json();
      const resultUrl = result.imageUrl ?? result.imageBase64;
      if (!resultUrl) throw new Error('No result image returned');

      setAppState('done');
      setTimeout(() => onSave(resultUrl), 300);
    } catch (err) {
      clearInterval(msgInterval);
      setErrorMsg(err instanceof Error ? err.message : 'AI inpainting failed');
      setAppState('error');
    }
  }, [hasMask, qualityMode, buildSourceBlob, buildMaskBlob, onSave]);

  // ── Keyboard shortcuts ────────────────────────────────────────
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (appState !== 'painting') return;
      if (e.key === 'Escape') onClose();
      if ((e.ctrlKey || e.metaKey) && e.key === 'z') { e.preventDefault(); undo(); }
      if (e.key === 'e' || e.key === 'E') setTool('erase');
      if (e.key === 'r' || e.key === 'R') setTool('restore');
      if (e.key === '[') setBrushSize((s) => Math.max(BRUSH_SIZES[0], s - 8));
      if (e.key === ']') setBrushSize((s) => Math.min(BRUSH_SIZES[BRUSH_SIZES.length - 1], s + 8));
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose, undo, appState]);

  const isProcessing = appState === 'processing';
  const { w: imgW, h: imgH } = imgSizeRef.current;

  // brushSize is in CANVAS pixels — on-screen ring size = brushSize * zoom.
  // Use zoom directly since canvas CSS width = imgW * zoom.
  const cursorRingSize = Math.max(4, brushSize * zoom);

  return (
    <div className="fixed inset-0 z-[100] bg-stone-950/97 flex flex-col overflow-hidden">
      <canvas ref={maskCanvasRef} className="hidden" />
      <canvas ref={origCanvasRef} className="hidden" />

      {/* ── Header ─────────────────────────────────────────────── */}
      <div className="h-14 bg-stone-900 border-b border-stone-800 flex items-center px-4 gap-3 shrink-0">
        <button
          onClick={onClose}
          disabled={isProcessing}
          className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-stone-400 hover:text-stone-200 hover:bg-stone-800 transition-colors cursor-pointer text-sm font-medium whitespace-nowrap disabled:opacity-40 disabled:cursor-not-allowed"
        >
          <i className="ri-arrow-left-line" /> Cancel
        </button>
        <div className="w-px h-6 bg-stone-800" />
        <div className="flex items-center gap-2">
          <div className="w-6 h-6 flex items-center justify-center rounded-lg bg-gradient-to-br from-violet-500 to-purple-600">
            <i className="ri-magic-fill text-white text-xs" />
          </div>
          <span className="text-sm font-bold text-stone-200 whitespace-nowrap">AI Smart Erase</span>
          <span className="text-xs text-stone-500 hidden sm:block">— Paint over what you want removed</span>
        </div>
        <div className="flex-1" />

        {!isProcessing && (
          <div className="hidden sm:flex items-center gap-2">
            <span className="text-xs text-stone-500 font-semibold whitespace-nowrap">Mode</span>
            <div className="flex items-center gap-1 bg-stone-800 rounded-lg p-1">
              <button
                onClick={() => setQualityMode('standard')}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold transition-colors cursor-pointer whitespace-nowrap ${qualityMode === 'standard' ? 'bg-stone-600 text-white' : 'text-stone-400 hover:text-stone-200'}`}
              >
                <i className="ri-flashlight-line" /> Standard
              </button>
              <button
                onClick={() => setQualityMode('high')}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold transition-colors cursor-pointer whitespace-nowrap ${qualityMode === 'high' ? 'bg-violet-600 text-white' : 'text-stone-400 hover:text-stone-200'}`}
              >
                <i className="ri-sparkling-2-line" /> High Quality
              </button>
            </div>
          </div>
        )}

        <div className="hidden sm:block w-px h-6 bg-stone-800" />

        <button
          onClick={handleSmartErase}
          disabled={!hasMask || isProcessing}
          className={`flex items-center gap-1.5 px-4 py-1.5 rounded-lg text-xs font-bold transition-all whitespace-nowrap ${
            hasMask && !isProcessing
              ? 'bg-violet-600 hover:bg-violet-500 text-white cursor-pointer'
              : 'bg-stone-700 text-stone-500 cursor-not-allowed'
          }`}
        >
          {isProcessing
            ? <><i className="ri-loader-4-line animate-spin" /> Processing…</>
            : <><i className="ri-magic-fill" /> Smart Erase</>}
        </button>
      </div>

      {/* ── Toolbar ─────────────────────────────────────────────── */}
      {!isProcessing && (
        <div className="bg-stone-900 border-b border-stone-800 px-4 py-2.5 flex items-center gap-4 flex-wrap shrink-0">
          <div className="flex items-center gap-1 bg-stone-800 rounded-lg p-1">
            <button
              onClick={() => setTool('erase')}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold transition-colors cursor-pointer whitespace-nowrap ${tool === 'erase' ? 'bg-red-500 text-white' : 'text-stone-400 hover:text-stone-200'}`}
            >
              <i className="ri-brush-line" /> Select
            </button>
            <button
              onClick={() => setTool('restore')}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold transition-colors cursor-pointer whitespace-nowrap ${tool === 'restore' ? 'bg-emerald-600 text-white' : 'text-stone-400 hover:text-stone-200'}`}
            >
              <i className="ri-eraser-line" /> Deselect
            </button>
          </div>

          <div className="w-px h-6 bg-stone-700" />

          <div className="flex items-center gap-2">
            <span className="text-xs text-stone-500 font-semibold whitespace-nowrap">Brush</span>
            <div className="flex items-center gap-1">
              {BRUSH_SIZES.map((size, i) => (
                <button
                  key={size}
                  onClick={() => setBrushSize(size)}
                  title={`${BRUSH_LABELS[i]} (${size}px)`}
                  className={`flex items-center justify-center rounded-md transition-colors cursor-pointer ${brushSize === size ? 'bg-red-500 text-white' : 'bg-stone-800 text-stone-400 hover:bg-stone-700 hover:text-stone-200'}`}
                  style={{ width: 32, height: 32 }}
                >
                  <div className="rounded-full bg-current" style={{ width: Math.max(4, size * 0.3), height: Math.max(4, size * 0.3) }} />
                </button>
              ))}
            </div>
            <span className="text-xs text-stone-600 font-mono whitespace-nowrap">{brushSize}px</span>
          </div>

          <div className="w-px h-6 bg-stone-700 hidden sm:block" />

          <div className="hidden sm:flex items-center gap-2">
            <span className="text-xs text-stone-500 font-semibold whitespace-nowrap">Zoom</span>
            <button onClick={() => setZoom((z) => Math.max(0.1, parseFloat((z - 0.15).toFixed(2))))} className="w-6 h-6 flex items-center justify-center rounded bg-stone-800 text-stone-400 hover:text-stone-200 cursor-pointer text-xs">
              <i className="ri-subtract-line" />
            </button>
            <span className="text-xs text-stone-400 font-mono w-10 text-center">{Math.round(zoom * 100)}%</span>
            <button onClick={() => setZoom((z) => Math.min(4, parseFloat((z + 0.15).toFixed(2))))} className="w-6 h-6 flex items-center justify-center rounded bg-stone-800 text-stone-400 hover:text-stone-200 cursor-pointer text-xs">
              <i className="ri-add-line" />
            </button>
            <button onClick={() => setZoom(1)} className="text-xs text-stone-600 hover:text-violet-400 transition-colors cursor-pointer whitespace-nowrap">Fit</button>
          </div>

          <div className="flex-1" />

          <div className="flex items-center gap-1">
            <button
              onClick={undo}
              disabled={!canUndo}
              className={`w-8 h-8 flex items-center justify-center rounded-lg transition-colors ${canUndo ? 'text-stone-400 hover:bg-stone-800 hover:text-stone-200 cursor-pointer' : 'text-stone-700 cursor-not-allowed'}`}
            >
              <i className="ri-arrow-go-back-line text-base" />
            </button>
            <button
              onClick={clearMask}
              disabled={!hasMask}
              className={`flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-semibold transition-colors ${hasMask ? 'text-stone-400 hover:bg-stone-800 hover:text-stone-200 cursor-pointer' : 'text-stone-700 cursor-not-allowed'}`}
            >
              <i className="ri-delete-bin-line" /> Clear
            </button>
          </div>

          <div className="hidden lg:flex items-center gap-1.5 text-xs text-stone-600">
            <i className="ri-information-line" />
            <span>E = Select · R = Deselect · [ ] = Brush · Ctrl+Z = Undo</span>
          </div>
        </div>
      )}

      {/* ── Canvas area ─────────────────────────────────────────── */}
      <div
        ref={containerRef}
        className="flex-1 overflow-auto bg-stone-950 relative"
        style={{
          backgroundImage: 'radial-gradient(circle, #292524 1px, transparent 1px)',
          backgroundSize: '24px 24px',
        }}
      >
        {/* Processing overlay */}
        {isProcessing && (
          <div className="absolute inset-0 z-20 flex flex-col items-center justify-center bg-stone-950/80 backdrop-blur-sm">
            <div className="flex flex-col items-center gap-6 max-w-sm text-center px-6">
              <div className="relative w-20 h-20 flex items-center justify-center">
                <div className="absolute inset-0 rounded-full border-4 border-stone-800" />
                <div className="absolute inset-0 rounded-full border-4 border-t-violet-500 border-r-violet-400 border-b-transparent border-l-transparent animate-spin" />
                <i className="ri-magic-fill text-2xl text-violet-400" />
              </div>
              <div>
                <p className="text-white font-bold text-lg mb-1">AI Inpainting</p>
                <p className="text-stone-400 text-sm">{progressMsg}</p>
              </div>
              <div className="w-full bg-stone-800 rounded-full h-1.5 overflow-hidden">
                <div className="h-full bg-gradient-to-r from-violet-600 to-purple-400 rounded-full animate-pulse" style={{ width: '70%' }} />
              </div>
              <p className="text-xs text-stone-600">
                {qualityMode === 'high' ? 'High Quality mode — this may take 15–30 seconds' : 'Standard mode — usually 8–15 seconds'}
              </p>
            </div>
          </div>
        )}

        {/* Error overlay */}
        {appState === 'error' && (
          <div className="absolute inset-0 z-20 flex flex-col items-center justify-center bg-stone-950/80 backdrop-blur-sm">
            <div className="flex flex-col items-center gap-4 max-w-sm text-center px-6">
              <div className="w-16 h-16 flex items-center justify-center rounded-full bg-red-950 border border-red-800">
                <i className="ri-error-warning-line text-2xl text-red-400" />
              </div>
              <div>
                <p className="text-white font-bold text-base mb-1">Inpainting Failed</p>
                <p className="text-stone-400 text-sm">{errorMsg}</p>
              </div>
              <button
                onClick={() => setAppState('painting')}
                className="px-5 py-2 rounded-lg bg-stone-800 hover:bg-stone-700 text-stone-200 text-sm font-semibold cursor-pointer transition-colors whitespace-nowrap"
              >
                Try Again
              </button>
            </div>
          </div>
        )}

        {/*
          IMPORTANT: Canvas is sized via CSS width/height = naturalSize * zoom.
          NO CSS transform is used. This means getBoundingClientRect() returns
          exactly what we expect, and coordinate math is simply: canvasPos = screenPos / zoom.
          The cursor ring is absolutely positioned inside the canvas wrapper div,
          which is exactly the same size as the canvas — so left/top coords map 1:1.
        */}
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'flex-start', minHeight: '100%', padding: 40 }}>
          {/* Canvas wrapper — exactly the same size as the canvas, position:relative for the cursor ring */}
          <div
            className="relative"
            style={{
              width: imgW ? imgW * zoom : 'auto',
              height: imgH ? imgH * zoom : 'auto',
              flexShrink: 0,
              overflow: 'visible',
            }}
          >
            <canvas
              ref={displayCanvasRef}
              style={{
                display: 'block',
                cursor: 'none',
                // Scale via CSS width/height — NOT transform. This keeps
                // getBoundingClientRect() honest and coordinate math simple.
                width: imgW ? imgW * zoom : undefined,
                height: imgH ? imgH * zoom : undefined,
                imageRendering: zoom > 2 ? 'pixelated' : 'auto',
                opacity: isProcessing ? 0.4 : 1,
                transition: 'opacity 0.3s',
              }}
              onMouseDown={handleMouseDown}
              onMouseMove={handleMouseMove}
              onMouseUp={handleMouseUp}
              onMouseLeave={handleMouseLeave}
              onTouchStart={handleTouchStart}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleTouchEnd}
            />

            {/* Cursor ring — position:absolute inside the canvas wrapper.
                cursorPos is (e.clientX - canvasRect.left, e.clientY - canvasRect.top),
                which is exactly the offset within the canvas element = offset within this wrapper. */}
            {cursorPos && !isProcessing && (
              <div
                className="absolute pointer-events-none"
                style={{
                  left: cursorPos.x,
                  top: cursorPos.y,
                  width: cursorRingSize,
                  height: cursorRingSize,
                  transform: 'translate(-50%, -50%)',
                  border: `2px solid ${tool === 'erase' ? '#ef4444' : '#10b981'}`,
                  borderRadius: '50%',
                  boxShadow: '0 0 0 1px rgba(0,0,0,0.5)',
                  zIndex: 10,
                  // Overflow visible so ring doesn't get clipped at edges
                  overflow: 'visible',
                }}
              />
            )}
          </div>
        </div>
      </div>

      {/* ── Status bar ───────────────────────────────────────────── */}
      <div className="h-9 bg-stone-900 border-t border-stone-800 flex items-center px-4 gap-4 shrink-0">
        {hasMask ? (
          <div className="flex items-center gap-2">
            <div className="w-2.5 h-2.5 rounded-full bg-red-500 animate-pulse" />
            <span className="text-xs text-stone-400">
              Selection active — click <strong className="text-violet-400">Smart Erase</strong> to remove with AI
            </span>
          </div>
        ) : (
          <div className="flex items-center gap-2">
            <div className="w-2.5 h-2.5 rounded-full bg-stone-600" />
            <span className="text-xs text-stone-500">Paint over the object you want to remove</span>
          </div>
        )}
        <div className="flex-1" />
        <div className="hidden sm:flex items-center gap-3 text-xs text-stone-600">
          <span className={`flex items-center gap-1 ${qualityMode === 'high' ? 'text-violet-400' : ''}`}>
            <i className={qualityMode === 'high' ? 'ri-sparkling-2-line' : 'ri-flashlight-line'} />
            {qualityMode === 'high' ? 'High Quality' : 'Standard'} mode
          </span>
          <span>·</span>
          <span>AI-powered content-aware removal</span>
        </div>
      </div>
    </div>
  );
}
